import React from "react";

import _ from "lodash";

import useDragAndDropList, { DragAndDropListHook } from "commons/hooks/useDragAndDropList";
import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { useConfigContext } from "context/config";

import useBlockGroupCompositionApi from "hooks/api/private/useBlockGroupCompositionApi";

import {
    BlockGroupCompositionBody,
    BlockGroupCompositionListItem,
} from "types/api/blockGroupComposition";
import { BlockEnum } from "types/enums";

import { createAdvancedListRequestFromFilter, filterEq } from "utils/utils";

export type NewBlockBodyForm = {
    selected_block: BlockEnum | undefined;
};

export type BlocksListItem = {
    item: BlockGroupCompositionListItem;
    deleted: boolean;
    openedBlock: boolean;
    focusedBlock: boolean;
};

export type BlocksFormHook = {
    setBlockGroupComposition: React.Dispatch<React.SetStateAction<BlocksListItem[]>>;
    onAddBlockClick: (
        blockName: BlockEnum,
        setOpenedSelectBlock: React.Dispatch<React.SetStateAction<boolean>>,
    ) => void;
    removeBlock: (blockGroupCompositionId: string) => void;
    blockGroupComposition: BlocksListItem[];
    dragAndDropHook: DragAndDropListHook<unknown>;
    formHook: FormHookType<NewBlockBodyForm>;
    blocksArray: Array<BlockEnum>;
};

export type HookParams = {
    blockGroupId: string;
};

function useBlocksForm({ blockGroupId }: HookParams): BlocksFormHook {
    const { disabledBlocks } = useConfigContext();

    const { getAllBlockGroupComposition, createBlockGroupComposition } =
        useBlockGroupCompositionApi();

    const [blockGroupComposition, setBlockGroupComposition] = React.useState<BlocksListItem[]>([]);

    React.useEffect(() => {
        const queryParams = createAdvancedListRequestFromFilter(
            filterEq("block_group_id", blockGroupId),
            "graphical_order,ASC",
        );

        const [apiCall] = getAllBlockGroupComposition(queryParams);

        apiCall.then((response) => {
            setBlockGroupComposition(
                response.content.map((item) => ({
                    item,
                    deleted: false,
                    openedBlock: false,
                    focusedBlock: false,
                })),
            );
        });
    }, [getAllBlockGroupComposition, blockGroupId]);

    const schema: ObjectPropertyType = React.useMemo(() => {
        return {
            type: "object",
            properties: {},
        };
    }, []);

    const initialValues: NewBlockBodyForm = React.useMemo(() => {
        return {
            selected_block: undefined,
        };
    }, []);

    const formHook = useForm<NewBlockBodyForm>(schema, initialValues);

    const blocksArray: Array<BlockEnum> = React.useMemo(
        (): Array<BlockEnum> =>
            Object.keys(BlockEnum).filter(
                (block) => disabledBlocks.indexOf(block as BlockEnum) === -1,
            ) as Array<BlockEnum>,
        [disabledBlocks],
    );

    const onAddBlockClick = React.useCallback(
        (
            blockName: BlockEnum,
            setOpenedSelectBlock: React.Dispatch<React.SetStateAction<boolean>>,
        ) => {
            if (blockName) {
                const graphicalOrder = blockGroupComposition.length * 10 + 10;

                const body: BlockGroupCompositionBody = {
                    block_group_id: blockGroupId,
                    block_id: blockName,
                    graphical_order: graphicalOrder,
                };

                const [apiCall] = createBlockGroupComposition(body);

                apiCall.then((response) => {
                    setBlockGroupComposition((prevValue) => {
                        const newValue = _.cloneDeep(prevValue);

                        if (blockName) {
                            newValue.push({
                                item: {
                                    id: response.id,
                                    block_group_id: blockGroupId,
                                    block_id: blockName,
                                    graphical_order: graphicalOrder,
                                },
                                deleted: false,
                                openedBlock: true,
                                focusedBlock: false,
                            });
                        }

                        return newValue;
                    });

                    setOpenedSelectBlock((prev) => !prev);
                });
            }
        },
        [createBlockGroupComposition, blockGroupComposition.length, blockGroupId],
    );

    const removeBlock = React.useCallback((blockGroupCompositionId: string) => {
        setBlockGroupComposition((prevValue) => {
            const newValue = _.cloneDeep(prevValue);

            const index = newValue.findIndex((value) => value.item.id === blockGroupCompositionId);

            if (index !== -1) {
                newValue[index].deleted = true;
            }

            return newValue;
        });
    }, []);

    const onUpdateList = React.useCallback(
        (newList: BlocksListItem[]) => {
            setBlockGroupComposition(() => {
                newList.forEach((block, index) => {
                    newList[index].item.graphical_order = (index + 1) * 10;
                });

                return newList;
            });
        },
        [setBlockGroupComposition],
    );

    const dragAndDropHook = useDragAndDropList({
        list: blockGroupComposition,
        onUpdateList: onUpdateList,
    });

    return React.useMemo(
        (): BlocksFormHook => ({
            setBlockGroupComposition,
            removeBlock,
            onAddBlockClick,
            blockGroupComposition,
            dragAndDropHook,
            blocksArray,
            formHook,
        }),
        [
            setBlockGroupComposition,
            removeBlock,
            onAddBlockClick,
            blockGroupComposition,
            dragAndDropHook,
            blocksArray,
            formHook,
        ],
    );
}

export default useBlocksForm;
